/**
 * ECS Client State Management
 * -----------------------------------------------------------------------------
 * Centralized state management for ECS client instance, configuration,
 * user account, and other module-level state.
 */

import type { IEcsClient } from "@1js/ecs-client";
import type { AppConfiguration, ConfigChangeListener } from "./types";
import { EcsClientStatus } from "./types";
import { errorDev } from '@/logging/devConsole';

// ECS Client instance
let ecsClient: IEcsClient | null = null;
let clientStatus: EcsClientStatus = EcsClientStatus.NotInitialized;

// Application configuration
let currentConfig: AppConfiguration | null = null;

// Fetch schedule state
let fetchScheduleActive: boolean = false;

// Config change listeners
const configChangeListeners: ConfigChangeListener[] = [];

// Last fetch timestamp
let lastFetchTimestamp: number | null = null;

/**
 * ECS Client Management
 */
export function getEcsClient(): IEcsClient | null {
  return ecsClient;
}

export function setEcsClient(client: IEcsClient | null): void {
  ecsClient = client;
  clientStatus = client ? EcsClientStatus.Ready : EcsClientStatus.NotInitialized;
}

export function getClientStatus(): EcsClientStatus {
  return clientStatus;
}

export function setClientStatus(status: EcsClientStatus): void {
  clientStatus = status;
}

/**
 * Configuration Management
 */
export function getCurrentConfig(): AppConfiguration | null {
  return currentConfig;
}

export function setCurrentConfig(config: AppConfiguration | null): void {
  currentConfig = config;
  if (config) {
    lastFetchTimestamp = Date.now();
  }
}

/**
 * Fetch Schedule Management
 */
export function isFetchScheduleActive(): boolean {
  return fetchScheduleActive;
}

export function setFetchScheduleActive(active: boolean): void {
  fetchScheduleActive = active;
}

/**
 * Listener Management
 */
export function addConfigChangeListener(listener: ConfigChangeListener): () => void {
  if (!configChangeListeners.includes(listener)) {
    configChangeListeners.push(listener);
  }
  
  // Return unsubscribe function
  return () => {
    const index = configChangeListeners.indexOf(listener);
    if (index > -1) {
      configChangeListeners.splice(index, 1);
    }
  };
}

export function removeConfigChangeListener(listener: ConfigChangeListener): void {
  const index = configChangeListeners.indexOf(listener);
  if (index > -1) {
    configChangeListeners.splice(index, 1);
  }
}

export function getConfigChangeListeners(): ConfigChangeListener[] {
  return [...configChangeListeners];
}

export function clearConfigChangeListeners(): void {
  configChangeListeners.length = 0;
}

export function notifyConfigChangeListeners(config: AppConfiguration): void {
  configChangeListeners.forEach(listener => {
    try {
      listener(config);
    } catch (error) {
      errorDev('[ECS-ERROR] Error in configuration change listener:', error);
    }
  });
}

/**
 * Timestamp Management
 */
export function getLastFetchTimestamp(): number | null {
  return lastFetchTimestamp;
}

export function setLastFetchTimestamp(timestamp: number): void {
  lastFetchTimestamp = timestamp;
}

/**
 * State Reset
 * Resets all module state to initial values.
 */
export function resetState(): void {
  ecsClient = null;
  clientStatus = EcsClientStatus.NotInitialized;
  currentConfig = null;
  fetchScheduleActive = false;
  configChangeListeners.length = 0;
  lastFetchTimestamp = null;
}

/**
 * Get complete state snapshot for debugging.
 */
export function getStateSnapshot(): {
  hasClient: boolean;
  clientStatus: EcsClientStatus;
  hasConfig: boolean;
  fetchScheduleActive: boolean;
  listenerCount: number;
  lastFetchTimestamp: number | null;
} {
  return {
    hasClient: ecsClient !== null,
    clientStatus,
    hasConfig: currentConfig !== null,
    fetchScheduleActive,
    listenerCount: configChangeListeners.length,
    lastFetchTimestamp,
  };
}